/**
 * Unit Converter
 *
 * Site-wide cm/inch toggle. Scans text nodes for tokens like `$80cm$`,
 * `$5mm$`, `$31.5inch$`, `$31.5"$`, and replaces them with spans whose
 * displayed unit follows the user's preference (localStorage).
 *
 * Token format: $<number><unit>$ where unit ∈ {cm, mm, inch, "}.
 * Strict closing `$` avoids collision with prices like `$80`.
 *
 * Public API:
 *   window.UnitConverter.toggle()
 *   window.UnitConverter.setUnit('cm' | 'inch')
 *   window.UnitConverter.getUnit()
 *
 * Custom event `unit:change` is dispatched on document for cross-component sync.
 */
(() => {
  'use strict';

  const STORAGE_KEY = 'variant-size-unit-preference';
  const DEFAULT_UNIT = 'cm';
  const TOKEN_REGEX = /\$(\d+(?:\.\d+)?)(?:\s*([~\-\/])\s*(\d+(?:\.\d+)?))?\s*(cm|mm|inch|")\$/gi;
  const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'INPUT', 'KBD', 'SAMP']);
  const SPAN_CLASS = 'convertible-unit';
  const EVENT_NAME = 'unit:change';

  const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
  const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* silent */ } };

  const toCm = (value, unit) => {
    const v = parseFloat(value);
    switch (unit.toLowerCase()) {
      case 'mm': return v / 10;
      case 'inch':
      case '"': return v * 2.54;
      case 'cm':
      default: return v;
    }
  };

  const trimZero = (n) => {
    const rounded = Math.round(n * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const formatValue = (cmMin, cmMax, separator, displayUnit) => {
    const isRange = cmMax !== undefined && !isNaN(cmMax);
    if (displayUnit === 'inch') {
      const inchMin = trimZero(cmMin / 2.54);
      if (isRange) {
        const inchMax = trimZero(cmMax / 2.54);
        return `${inchMin}${separator}${inchMax} inch`;
      }
      return `${inchMin} inch`;
    }
    const cmMinStr = trimZero(cmMin);
    if (isRange) {
      const cmMaxStr = trimZero(cmMax);
      return `${cmMinStr}${separator}${cmMaxStr} cm`;
    }
    return `${cmMinStr} cm`;
  };

  let currentUnit = safeGet(STORAGE_KEY);
  if (currentUnit !== 'cm' && currentUnit !== 'inch') currentUnit = DEFAULT_UNIT;

  const rerenderAll = () => {
    document.querySelectorAll(`.${SPAN_CLASS}`).forEach((el) => {
      const cmMin = parseFloat(el.dataset.cm);
      if (isNaN(cmMin)) return;
      const cmMax = el.dataset.cmMax ? parseFloat(el.dataset.cmMax) : undefined;
      const separator = el.dataset.separator || '';
      el.textContent = formatValue(cmMin, cmMax, separator, currentUnit);
    });
  };

  const setUnit = (unit) => {
    if (unit !== 'cm' && unit !== 'inch') return;
    if (currentUnit === unit) return;
    currentUnit = unit;
    safeSet(STORAGE_KEY, unit);
    rerenderAll();
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { unit } }));
  };

  const toggle = () => setUnit(currentUnit === 'cm' ? 'inch' : 'cm');

  const isInsideSkipped = (node) => {
    let p = node.parentNode;
    while (p && p.nodeType === Node.ELEMENT_NODE) {
      if (SKIP_TAGS.has(p.tagName)) return true;
      if (p.isContentEditable) return true;
      if (p.classList && p.classList.contains(SPAN_CLASS)) return true;
      p = p.parentNode;
    }
    return false;
  };

  const processTextNode = (node) => {
    const text = node.nodeValue;
    if (!text || text.indexOf('$') === -1) return;
    TOKEN_REGEX.lastIndex = 0;
    if (!TOKEN_REGEX.test(text)) return;
    TOKEN_REGEX.lastIndex = 0;

    const frag = document.createDocumentFragment();
    let last = 0;
    let m;
    while ((m = TOKEN_REGEX.exec(text)) !== null) {
      const [match, val1, separator, val2, unit] = m;
      const idx = m.index;
      if (idx > last) frag.appendChild(document.createTextNode(text.slice(last, idx)));
      const cmMin = toCm(val1, unit);
      const cmMax = val2 ? toCm(val2, unit) : undefined;
      const span = document.createElement('span');
      span.className = SPAN_CLASS;
      span.dataset.cm = cmMin;
      if (cmMax !== undefined) {
        span.dataset.cmMax = cmMax;
        span.dataset.separator = separator || '~';
      }
      span.textContent = formatValue(cmMin, cmMax, separator, currentUnit);
      span.setAttribute('role', 'button');
      span.setAttribute('tabindex', '0');
      span.setAttribute('aria-label', 'Toggle unit (cm / inch)');
      frag.appendChild(span);
      last = idx + match.length;
    }
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    node.parentNode.replaceChild(frag, node);
  };

  const scanRoot = (root) => {
    if (!root) return;
    if (root.nodeType === Node.TEXT_NODE) {
      if (!isInsideSkipped(root)) processTextNode(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
    if (root.nodeType === Node.ELEMENT_NODE && SKIP_TAGS.has(root.tagName)) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => {
        if (!n.nodeValue || n.nodeValue.indexOf('$') === -1) return NodeFilter.FILTER_REJECT;
        if (isInsideSkipped(n)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    const batch = [];
    while (walker.nextNode()) batch.push(walker.currentNode);
    batch.forEach(processTextNode);
  };

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((n) => {
        if (n.nodeType === Node.ELEMENT_NODE) scanRoot(n);
        else if (n.nodeType === Node.TEXT_NODE && !isInsideSkipped(n)) processTextNode(n);
      });
    }
  });

  const handleActivation = (target) => {
    if (target && target.classList && target.classList.contains(SPAN_CLASS)) {
      toggle();
      return true;
    }
    return false;
  };

  const init = () => {
    scanRoot(document.body);
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', (e) => handleActivation(e.target));
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (handleActivation(e.target)) e.preventDefault();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Cross-component sync: external dispatchers (e.g. variant-size-info Alpine) can fire `unit:change`.
  document.addEventListener(EVENT_NAME, (e) => {
    const u = e.detail && e.detail.unit;
    if (u && u !== currentUnit) setUnit(u);
  });

  window.UnitConverter = { toggle, setUnit, getUnit: () => currentUnit };
})();
